import { ChatMessage, ConversationState } from "@/components/agent/chat/types";
import { Tables } from "@/types/database.types";

// Shared interfaces for conversation hooks
export interface DbMessage {
  id: string;
  sender_type: "ai" | "user" | "agent_chat_user";
  content: string;
  created_at: string;
  metadata?: { responseId?: string; is_system_message?: boolean } | null;
  openai_message_id?: string | null;
}

export interface ConversationData {
  id: string;
  agent_id: string;
  created_at: string;
  updated_at: string;
  state: ConversationState;
  summary: string | null;
  user_id: string | null;
  agent_chat_user_id: string | null;
  messages: Array<{
    id: string;
    sender_type: "ai" | "user" | "agent_chat_user";
    content: string;
    created_at: string;
    metadata?: { responseId?: string; is_system_message?: boolean } | null;
    openai_message_id?: string | null;
  }>;
  agent: Tables<"agents">;
  responseId?: string | null;
}

export interface ConversationHookResult {
  conversation: ConversationData | null;
  isLoading: boolean;
  error: string | null;
  conversationId: string | null;
  initialMessages: ChatMessage[];
  previousResponseId: string | null;
}

/**
 * Processes database messages and converts them to UI format
 * Extracts the previous response ID from the last assistant message
 */
export function processConversationMessages(messages: any[]) {
  const processedMessages: ChatMessage[] = [];
  let responseId: string | null = null;

  if (messages && Array.isArray(messages)) {
    messages.forEach((msg) => {
      if (
        msg &&
        "sender_type" in msg &&
        "content" in msg &&
        typeof msg.content === "string"
      ) {
        const typedMessage = msg as unknown as DbMessage;

        // Map the sender_type to UI message type
        let messageType: "user" | "agent" | "system" = "agent";

        if (
          typedMessage.sender_type === "user" ||
          typedMessage.sender_type === "agent_chat_user"
        ) {
          messageType = "user";
        } else if (typedMessage.sender_type === "ai") {
          // Check if it's a system message
          if (
            typedMessage.metadata &&
            typeof typedMessage.metadata === "object" &&
            typedMessage.metadata.is_system_message
          ) {
            messageType = "system";
          } else {
            messageType = "agent";
          }
        }

        processedMessages.push({
          type: messageType,
          content: typedMessage.content,
          timestamp: typedMessage.created_at || new Date().toISOString(),
        });
      }
    });

    // Find last assistant message to get responseId
    const assistantMessages = messages.filter(
      (m) => m && "sender_type" in m && m.sender_type === "ai"
    );

    if (assistantMessages.length > 0) {
      const lastAssistantMessage = assistantMessages[
        assistantMessages.length - 1
      ] as unknown as DbMessage;

      // Use openai_message_id as the responseId for conversation continuity
      if (lastAssistantMessage.openai_message_id) {
        responseId = lastAssistantMessage.openai_message_id;
      }
    }
  }

  return {
    messages: processedMessages,
    previousResponseId: responseId,
  };
}

/**
 * Normalizes conversation state to ensure it's properly typed
 */
export function normalizeConversationState(state: any): ConversationState {
  return state === "completed" ? "completed" : "open";
}

/**
 * Creates an enriched conversation object with normalized state and response ID
 * Uses proper object construction instead of unsafe type assertions
 */
export function createEnrichedConversation(
  conversationData: any,
  responseId: string | null
): ConversationData {
  const state = normalizeConversationState(conversationData.state);
  
  // Validate and construct the object safely
  const enrichedConversation: ConversationData = {
    id: conversationData.id,
    agent_id: conversationData.agent_id,
    created_at: conversationData.created_at,
    updated_at: conversationData.updated_at,
    state,
    summary: conversationData.summary,
    user_id: conversationData.user_id,
    agent_chat_user_id: conversationData.agent_chat_user_id,
    messages: conversationData.messages || [],
    agent: conversationData.agent,
    responseId,
  };
  
  return enrichedConversation;
} 